import { z } from 'zod';

// ---------------------------------------------------------------------------
// Work relationships — how two pieces of work relate, and on whose authority.
//
// This is the successor contract for the retired `/api/impact/duplicates`
// (#2876), whose parity row records why it was dropped rather than migrated: it
// ran unmetered model calls over caller-supplied similarity guesses, so nothing
// it emitted could be traced back to a fact. The fix is not a better model; it
// is a contract that says, per edge, WHO asserted it.
//
// Every edge therefore carries `provenance`, and the discrimination is load-
// bearing rather than decorative:
//
//   `provider-asserted` — GitHub or Linear recorded this edge. A Linear
//     duplicate relation, a `Closes #N` delivery claim, a timeline cross-
//     reference. Someone stated it; kruxit only normalised it.
//
//   `derived` — kruxit inferred it from bounded, deterministic evidence
//     (changed-path overlap per the `kruxit-area` lineage, or title-term
//     similarity). Real evidence, weaker authority, and never presented as a
//     provider fact.
//
// Nothing here is model-derived. The derived signals are pure functions of
// fields already fetched, so the same board always yields the same edges; if a
// model signal is ever added it must arrive through the metered `getModel()`
// path (#2584) and take its own `derived` kind rather than borrowing one of
// these.
// ---------------------------------------------------------------------------

export const WORK_RELATIONSHIP_PROVENANCES = [
  'provider-asserted',
  'derived',
] as const;
export const WorkRelationshipProvenanceSchema = z.enum(
  WORK_RELATIONSHIP_PROVENANCES,
);
export type WorkRelationshipProvenance = z.infer<
  typeof WorkRelationshipProvenanceSchema
>;

/**
 * Edges a provider recorded. `duplicate-of` / `duplicated-by` and `related` come
 * from Linear issue relations; `delivers` / `delivered-by` from a `Closes #N`-
 * style delivery claim in an issue or pull-request body (including one harvested
 * from an open cross-referenced PR, which is a GitHub timeline fact);
 * `shared-delivery-target` is the duplicate-work case that matters most — two
 * open items each claiming to close the SAME target.
 */
export const ASSERTED_WORK_RELATIONSHIP_KINDS = [
  'duplicate-of',
  'duplicated-by',
  'shared-delivery-target',
  'delivers',
  'delivered-by',
  'blocks',
  'blocked-by',
  'related',
] as const;
export const AssertedWorkRelationshipKindSchema = z.enum(
  ASSERTED_WORK_RELATIONSHIP_KINDS,
);
export type AssertedWorkRelationshipKind = z.infer<
  typeof AssertedWorkRelationshipKindSchema
>;

/**
 * Edges kruxit inferred. `file-overlap` reuses the `kruxit-area` path lineage
 * (#2528): scope-qualified changed paths, compared with the same glob matcher
 * and the same "at least two identifying segments" rule the planner's conflict
 * pass uses. `topic-similarity` is a bounded title-term comparison — no model,
 * no body text, and gated on a shared-term floor so it cannot fire on two items
 * that merely share ordinary English.
 */
export const DERIVED_WORK_RELATIONSHIP_KINDS = [
  'file-overlap',
  'topic-similarity',
] as const;
export const DerivedWorkRelationshipKindSchema = z.enum(
  DERIVED_WORK_RELATIONSHIP_KINDS,
);
export type DerivedWorkRelationshipKind = z.infer<
  typeof DerivedWorkRelationshipKindSchema
>;

export const WorkRelationshipKindSchema = z.enum([
  ...ASSERTED_WORK_RELATIONSHIP_KINDS,
  ...DERIVED_WORK_RELATIONSHIP_KINDS,
]);
export type WorkRelationshipKind = z.infer<typeof WorkRelationshipKindSchema>;

/** Which provider fact carried an asserted edge — the thing a reader can check. */
export const AssertedWorkRelationshipSourceSchema = z.enum([
  'linear-relation',
  'github-cross-reference',
  'delivery-claim',
]);
export type AssertedWorkRelationshipSource = z.infer<
  typeof AssertedWorkRelationshipSourceSchema
>;

/** Which bounded comparison produced a derived edge. */
export const DerivedWorkRelationshipSignalSchema = z.enum([
  'shared-changed-paths',
  'shared-title-terms',
]);
export type DerivedWorkRelationshipSignal = z.infer<
  typeof DerivedWorkRelationshipSignalSchema
>;

/**
 * The other end of an edge. `id` is present only when the target is itself on
 * the board — an edge can legitimately name work the viewer cannot see, and
 * saying so beats silently dropping it.
 */
export const WorkRelationshipTargetSchema = z.object({
  /** `BoardItem.id`, when the target is on the board. */
  id: z.string().min(1).optional(),
  /** Human reference: `#123` (GitHub) or `ENG-45` (Linear). */
  key: z.string().min(1),
  /** `owner/repo` (GitHub) or `Workspace/TEAM` (Linear). */
  scope: z.string().min(1),
  url: z.string().optional(),
  /**
   * Lifecycle of the target as best known. `unknown` when the target is off the
   * board and the provider fact carried no state — which is why a consumer must
   * never read "not closed" as "open".
   */
  state: z.enum(['open', 'closed', 'merged', 'unknown']),
});
export type WorkRelationshipTarget = z.infer<
  typeof WorkRelationshipTargetSchema
>;

const WorkRelationshipBaseSchema = z.object({
  /** `BoardItem.id` of the item this edge hangs off. */
  from: z.string().min(1),
  to: WorkRelationshipTargetSchema,
  /** Bounded, DX-facing English naming the concrete evidence. */
  evidence: z.string().min(1).max(160),
});

export const AssertedWorkRelationshipSchema = WorkRelationshipBaseSchema.extend(
  {
    provenance: z.literal('provider-asserted'),
    kind: AssertedWorkRelationshipKindSchema,
    source: AssertedWorkRelationshipSourceSchema,
  },
);
export type AssertedWorkRelationship = z.infer<
  typeof AssertedWorkRelationshipSchema
>;

export const DerivedWorkRelationshipSchema = WorkRelationshipBaseSchema.extend({
  provenance: z.literal('derived'),
  kind: DerivedWorkRelationshipKindSchema,
  signal: DerivedWorkRelationshipSignalSchema,
  /**
   * How much of the compared evidence the two items share, rounded to two
   * decimals so the value is stable across runs. Ordering only — it is NOT a
   * probability that the work is duplicated.
   */
  strength: z.number().min(0).max(1),
});
export type DerivedWorkRelationship = z.infer<
  typeof DerivedWorkRelationshipSchema
>;

export const WorkRelationshipSchema = z.discriminatedUnion('provenance', [
  AssertedWorkRelationshipSchema,
  DerivedWorkRelationshipSchema,
]);
export type WorkRelationship = z.infer<typeof WorkRelationshipSchema>;

/**
 * How many edges one item carries on the board. Small on purpose: the row chip
 * and the MCP block are an alert that overlapping work exists, not a report. An
 * item that overlaps a dozen others is itself the finding, and the count says so
 * without shipping a dozen edges to every surface.
 */
export const WORK_RELATIONSHIP_LIMIT = 5;

/**
 * Minimum share of compared title terms before a `topic-similarity` edge is
 * emitted, alongside {@link TOPIC_SIMILARITY_MIN_SHARED_TERMS}. Both gates apply:
 * a high ratio over one shared word is noise, and two shared words out of forty
 * is coincidence.
 */
export const TOPIC_SIMILARITY_MIN_STRENGTH = 0.4;
export const TOPIC_SIMILARITY_MIN_SHARED_TERMS = 2;
